import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BILLING_MODE_LABELS,
  FRANCHISE_PAYER_LABELS,
  MODULE_LABELS,
  SUBSCRIPTION_MULTI_MODULES,
  PLAN_LABELS,
  type CheckoutResponse,
  type DunningStage,
  type FranchisePayer,
  type InAppModule,
  type MyPlanDTO,
  type SeatStatus,
  type TermsStatusDTO,
} from '@bifeai/shared';
import { Badge, Button, Callout, Card, CardBody, CardHeader, Field, Input, PageHeader, Select, Skeleton, useToast } from '@bifeai/ui';
import { CreditCard, Download, ExternalLink, Infinity as InfinityIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, downloadFile, errorMessage } from '../lib/api';
import { formatDate, formatUsd } from '../lib/format';
import { useSession } from '../lib/session';

const MODULE_HINTS: Partial<Record<InAppModule, string>> = {
  CUTTING: 'Reemplaza al desposte manual: rinde cada media res sola y te dice qué cortes te dejan más.',
  ATTENDANCE: 'Entrada y salida del personal, horas trabajadas y llegadas tarde.',
  FRANCHISES: 'Para sumar franquicias: pedidos a la marca, cuenta corriente, regalías y facturación entre la marca y cada franquicia.',
  ORDERS_CHAT:
    'bifi, el agente de WhatsApp especializado en el rubro de tu carnicería: entiende textos y audios, contesta los mensajes que recibas, vende las 24 hs, genera venta cruzada, arregla la hora de entrega o retiro, envía el link de pago de Mercado Pago, te deja el pedido en el tablero listo para armar y le avisa al cliente cuando está listo. Se activa con un pago único y lleva un abono mensual por cada número de WhatsApp.',
  WHOLESALE:
    'Para vender por mayor a restaurantes, carnicerías y otros negocios: listas de precios por cliente, pedidos con kilos reales, hojas de ruta, el repartidor en el celular aunque no haya señal, cobros y rendición, comisiones y el remito electrónico cárnico de ARCA. Se activa con un pago único y lleva un abono mensual.',
};

/** Qué pasa en cada etapa del atraso (lo que ve el cliente). */
function dunningText(
  stage: DunningStage,
  until: string,
  who: 'cuenta' | 'franquicia',
  monthly = false,
): { tone: 'warning' | 'danger'; title: string; text: string } {
  const d = formatDate(until);
  switch (stage) {
    case 'GRACE':
      return {
        tone: 'warning',
        title:
          who === 'cuenta'
            ? monthly
              ? 'No pudimos cobrar tu abono mensual'
              : 'No pudimos cobrar tu mantenimiento anual'
            : monthly
              ? 'Tenés el abono de tu franquicia sin pagar'
              : 'Tenés la licencia de tu franquicia sin pagar',
        text: `Seguís usando todo normalmente hasta el ${d}. Después, la ${who} pasa a solo lectura.`,
      };
    case 'READ_ONLY':
      return {
        tone: 'danger',
        title: `Tu ${who} está en solo lectura`,
        text: `Podés ver y descargar tus datos, pero no vender ni cargar nada. Apenas pagás, vuelve a funcionar. Si no, el ${d} se da de baja.`,
      };
    case 'SUSPENDED':
      return {
        tone: 'danger',
        title: `Tu ${who} fue dada de baja por falta de pago`,
        text: `Tenés hasta el ${d} para renovar o descargar tus datos.`,
      };
    default:
      return { tone: 'danger', title: `Tu ${who} está dada de baja`, text: 'Escribinos a soporte@bifeai.com para recuperarla.' };
  }
}

const SEAT_LABEL: Record<SeatStatus, { text: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  ACTIVE: { text: 'Al día', tone: 'success' },
  PENDING: { text: 'Pendiente de pago', tone: 'warning' },
  PAST_DUE: { text: 'Pago rechazado', tone: 'danger' },
  CANCELED: { text: 'Dada de baja', tone: 'neutral' },
};

/** Mi plan: lo que tiene la carnicería, lo que paga (por año o por mes) y lo que puede sumar. */
export function PlanPage() {
  const { me, refresh, isHeadquartersUser } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const paid = params.get('pago') === 'ok';
  const plan = useQuery({ queryKey: ['my-plan'], queryFn: () => api.get<MyPlanDTO>('/billing/plan') });

  // Al volver de Stripe: el pago se confirma en unos segundos (aviso de Stripe). Se actualiza solo.
  useEffect(() => {
    if (!paid) return;
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      await Promise.all([qc.invalidateQueries({ queryKey: ['my-plan'] }), refresh()]);
      if (n >= 8) clearInterval(t);
    }, 2500);
    return () => clearInterval(t);
  }, [paid, qc, refresh]);

  const go = async (p: Promise<CheckoutResponse>) => {
    try {
      window.location.assign((await p).url);
    } catch (err) {
      toast.error('No se pudo abrir el pago', errorMessage(err));
    }
  };
  const [busy, setBusy] = useState<string | null>(null);
  const run = (key: string, p: () => Promise<CheckoutResponse>) => async () => {
    setBusy(key);
    await go(p());
    setBusy(null);
  };
  const portal = run('portal', () => api.post<CheckoutResponse>('/billing/portal'));
  const payNow = run('pay', () => api.post<CheckoutResponse>('/billing/pay'));
  const [exporting, setExporting] = useState(false);
  const exportData = async () => {
    setExporting(true);
    try {
      await downloadFile('/export/full', 'bifeai-datos.zip');
    } catch (err) {
      toast.error('No se pudo descargar', errorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  const payer = useMutation({
    mutationFn: (p: FranchisePayer) => api.put('/billing/franchise-payer', { payer: p }),
    onSuccess: async () => {
      toast.success('Listo', 'Rige para las franquicias que sumes desde ahora.');
      await qc.invalidateQueries({ queryKey: ['my-plan'] });
    },
    onError: (err) => toast.error('No se pudo cambiar', errorMessage(err)),
  });

  const header = <PageHeader title="Mi plan" subtitle="Tu plan, lo que pagás y lo que podés sumar." />;
  const subscriptionAction = useMutation({
    mutationFn: (action: 'cancel' | 'resume') => api.post<MyPlanDTO>(`/billing/subscription/${action}`),
    onSuccess: async (data, action) => {
      qc.setQueryData(['my-plan'], data);
      toast.success(
        action === 'cancel' ? 'Abono dado de baja' : 'Seguís con el abono',
        action === 'cancel' && data.cancelAt ? `Deja de cobrarse el ${formatDate(data.cancelAt)}. Hasta ese día todo sigue igual.` : undefined,
      );
    },
    onError: (err) => toast.error('No se pudo hacer', errorMessage(err)),
  });
  const banner = paid && (
    <Callout tone="success">
      <strong>¡Pago recibido!</strong> En unos segundos queda activo.{' '}
      <button type="button" className="bf-link-btn" onClick={() => setParams({})}>
        Cerrar
      </button>
    </Callout>
  );

  if (plan.isLoading) {
    return (
      <>
        {header}
        <Skeleton style={{ height: 220 }} />
      </>
    );
  }
  if (plan.isError || !plan.data) {
    return (
      <>
        {header}
        <Callout tone="danger">{errorMessage(plan.error)}</Callout>
      </>
    );
  }
  const p = plan.data;

  // ---------------------------------------------------------------- franquiciado
  if (!isHeadquartersUser) {
    const s = p.seat;
    return (
      <>
        {header}
        {banner}
        {s?.dunning && <DunningCallout {...dunningText(s.dunning.stage, s.dunning.until, 'franquicia', s.monthly)} />}
        <Card>
          <CardHeader title={s?.monthly || p.mode === 'SUBSCRIPTION' ? 'Abono de tu franquicia' : 'Licencia de tu franquicia'} />
          <CardBody>
            {!s ? (
              <p style={{ margin: 0 }}>
                {p.mode === 'SUBSCRIPTION' ? 'El abono' : 'La licencia'} de tu franquicia la paga la marca. No tenés nada para pagar.
              </p>
            ) : (
              <div className="bf-form" style={{ gap: 14 }}>
                <Facts>
                  <Fact label="Estado">
                    <Badge tone={SEAT_LABEL[s.status].tone} dot>
                      {SEAT_LABEL[s.status].text}
                    </Badge>
                  </Fact>
                  <Fact label="Precio">
                    {formatUsd(s.yearlyCents)} por {s.monthly ? 'mes' : 'año'}
                  </Fact>
                  {s.renewal && <Fact label="Renovación">{formatDate(s.renewal)}</Fact>}
                </Facts>
                {(s.status === 'PENDING' || s.status === 'CANCELED') && (
                  <>
                    <p className="bf-hint" style={{ margin: 0 }}>
                      {s.monthly
                        ? 'Pagás el primer mes ahora con tarjeta y después se cobra solo cada mes.'
                        : 'Pagás el primer año ahora con tarjeta y después se renueva solo cada año.'}{' '}
                      Lo cobra BifeAI (Yann Technology Group LLC).
                    </p>
                    <div>
                      <Button
                        variant="primary"
                        icon={<CreditCard />}
                        loading={busy === 'seat'}
                        disabled={!p.ready}
                        onClick={run('seat', () => api.post<CheckoutResponse>('/billing/seat/checkout'))}
                      >
                        Pagar {formatUsd(s.yearlyCents)}
                      </Button>
                    </div>
                  </>
                )}
                {(s.status === 'ACTIVE' || s.status === 'PAST_DUE') && (
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {s.status === 'PAST_DUE' && (
                      <Button variant="primary" icon={<CreditCard />} loading={busy === 'pay'} onClick={payNow}>
                        Pagar ahora
                      </Button>
                    )}
                    <Button icon={<ExternalLink />} loading={busy === 'portal'} onClick={portal}>
                      Tarjeta y facturas
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>
        {p.bifi && <BifiCard bifi={p.bifi} franchise busy={busy} onRun={run} onPortal={portal} ready={p.ready} />}
      </>
    );
  }

  // ---------------------------------------------------------------- casa central
  const franchises = me.tenant.modules.includes('FRANCHISES');
  const monthly = p.mode === 'SUBSCRIPTION';
  const switchToLicense = run('license', () => api.post<CheckoutResponse>('/billing/subscription/switch-to-license'));
  const cancel = () => {
    const until = p.renewal ? ` el ${formatDate(p.renewal)}` : ' al terminar el mes';
    if (
      window.confirm(
        `¿Dar de baja el abono? Deja de cobrarse${until} y hasta ese día seguís usando todo normalmente. Después la cuenta queda en solo lectura (podés ver y descargar tus datos).`,
      )
    ) {
      subscriptionAction.mutate('cancel');
    }
  };
  const active = (['MULTI_BRANCH', 'ARCA', 'FRANCHISES', 'CUTTING', 'ATTENDANCE', 'ORDERS_CHAT'] as const).filter((m) =>
    me.tenant.modules.includes(m),
  );

  return (
    <>
      {header}
      {banner}
      {p.dunning && (
        <DunningCallout {...dunningText(p.dunning.stage, p.dunning.until, 'cuenta', p.mode === 'SUBSCRIPTION')}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            <Button variant="primary" loading={busy === 'pay'} onClick={payNow}>
              Pagar ahora{p.dunning.amountCents ? ` ${formatUsd(p.dunning.amountCents)}` : ''}
            </Button>
            <Button loading={busy === 'portal'} onClick={portal}>
              Cambiar la tarjeta
            </Button>
          </div>
        </DunningCallout>
      )}
      {p.wholesaleDunning && (
        <DunningCallout
          tone={p.wholesaleDunning.cut ? 'danger' : 'warning'}
          title={p.wholesaleDunning.cut ? 'El Mayorista está apagado' : 'No pudimos cobrar el abono del Mayorista'}
          text={
            p.wholesaleDunning.cut
              ? 'El Mayorista se apagó porque falta pagar el abono mensual. Todos tus clientes, pedidos y cuentas quedaron guardados: apenas se pague, vuelve solo tal cual estaba.'
              : `El Mayorista sigue funcionando hasta el ${formatDate(p.wholesaleDunning.cutsOn)}. Si para entonces no se paga el abono mensual, se apaga (los datos quedan guardados).`
          }
        >
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            <Button loading={busy === 'portal'} onClick={portal}>
              Pagar o cambiar la tarjeta
            </Button>
          </div>
        </DunningCallout>
      )}
      {p.bifiDunning && (
        <DunningCallout
          tone={p.bifiDunning.paused ? 'danger' : 'warning'}
          title={p.bifiDunning.paused ? 'bifi está pausado' : 'No pudimos cobrar el abono de bifi'}
          text={
            p.bifiDunning.paused
              ? 'bifi no está contestando porque falta pagar el abono mensual. Los mensajes siguen llegando al chat. Apenas se pague, vuelve a contestar solo.'
              : `bifi sigue contestando hasta el ${formatDate(p.bifiDunning.pausesOn)}. Si para entonces no se paga el abono mensual, se pausa (los mensajes siguen llegando al chat).`
          }
        >
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            <Button loading={busy === 'portal'} onClick={portal}>
              Pagar o cambiar la tarjeta
            </Button>
          </div>
        </DunningCallout>
      )}
      <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', alignItems: 'start' }}>
        <Card>
          <CardHeader title={p.plan === 'BIFI' ? 'Tu plan' : monthly ? 'Tu abono' : 'Tu licencia'} />
          <CardBody>
            <div className="bf-form" style={{ gap: 14 }}>
              <Facts>
                <Fact label="Plan">
                  <strong>{PLAN_LABELS[p.plan]}</strong>
                  {franchises && p.plan === 'MULTISUCURSAL' ? ' + Franquicias' : ''}
                </Fact>
                {p.plan !== 'BIFI' && <Fact label="Forma de pago">{BILLING_MODE_LABELS[p.mode]}</Fact>}
                {p.status && (
                  <Fact label="Estado">
                    <Badge tone={p.status === 'ACTIVE' ? 'success' : 'danger'} dot>
                      {p.status === 'ACTIVE' ? 'Al día' : p.status === 'PAST_DUE' ? 'Pago pendiente' : 'Dada de baja'}
                    </Badge>
                  </Fact>
                )}
                {p.renewal && p.plan !== 'BIFI' && (
                  <Fact label={monthly ? (p.cancelAt ? 'Deja de cobrarse' : 'Próximo cobro') : 'Renovación'}>{formatDate(p.renewal)}</Fact>
                )}
                {p.billed && p.plan !== 'BIFI' && !monthly && <Fact label="Mantenimiento anual">{formatUsd(p.yearlyCents)}</Fact>}
                {p.billed && monthly && <Fact label="Abono por mes">{formatUsd(p.monthlyCents)}</Fact>}
              </Facts>
              {monthly && p.billed && p.cancelAt && (
                <Callout tone="warning">
                  Diste de baja el abono: deja de cobrarse el <strong>{formatDate(p.cancelAt)}</strong> y hasta ese día todo sigue igual. Después
                  la cuenta queda en solo lectura.
                </Callout>
              )}
              {monthly && p.billed && !p.cancelAt && (
                <p className="bf-hint" style={{ margin: 0 }}>
                  Sin permanencia: lo podés dar de baja cuando quieras y deja de cobrarse al terminar el mes que ya pagaste.
                </p>
              )}
              {p.plan === 'BIFI' && (
                <p className="bf-hint" style={{ margin: 0 }}>
                  Tenés bifi sin el resto de la plataforma: sin licencia ni mantenimiento anual, pagás el abono mensual por número de
                  WhatsApp. Locales y franquicias se suman sin costo. Si querés la plataforma completa, escribinos.
                </p>
              )}
              {p.billed || (p.plan === 'BIFI' && p.bifi && p.bifi.lines > 0) ? (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <Button icon={<ExternalLink />} loading={busy === 'portal'} onClick={portal}>
                    Tarjeta y facturas
                  </Button>
                  {monthly && p.billed && me.membership.role === 'OWNER' && p.status !== 'CANCELED' && (
                    <Button
                      variant={p.cancelAt ? 'primary' : 'ghost'}
                      loading={subscriptionAction.isPending}
                      onClick={() => (p.cancelAt ? subscriptionAction.mutate('resume') : cancel())}
                    >
                      {p.cancelAt ? 'Seguir con el abono' : 'Dar de baja el abono'}
                    </Button>
                  )}
                </div>
              ) : (
                <p className="bf-hint" style={{ margin: 0 }}>
                  Tu cuenta la administra BifeAI. Para cambios escribinos a <a href="mailto:soporte@bifeai.com">soporte@bifeai.com</a>.
                </p>
              )}
            </div>
          </CardBody>
        </Card>

        {p.licenseOffer && (
          <Card>
            <CardHeader title="Pasate a la licencia de por vida" subtitle="Pagás una vez y el sistema es tuyo." />
            <CardBody>
              <div className="bf-form" style={{ gap: 14 }}>
                <Facts>
                  <Fact label="Licencia (pago único)">{formatUsd(p.licenseOffer.amountCents)}</Fact>
                  <Fact label="Mantenimiento anual">{formatUsd(p.licenseOffer.yearlyCents)} desde el segundo año</Fact>
                </Facts>
                <p className="bf-hint" style={{ margin: 0 }}>
                  Te llevás todo lo que usás hoy
                  {p.plan === 'MULTISUCURSAL' && me.tenant.modules.some((m) => SUBSCRIPTION_MULTI_MODULES.includes(m))
                    ? ', con Desposte y Asistencia incluidos en el precio'
                    : ''}
                  . El primer año de mantenimiento va incluido y el abono deja de cobrarse apenas se confirma el pago.
                </p>
                {me.membership.role === 'OWNER' && (
                  <div>
                    <Button
                      variant="primary"
                      icon={<InfinityIcon />}
                      loading={busy === 'license'}
                      disabled={p.status === 'PAST_DUE'}
                      onClick={switchToLicense}
                    >
                      Pasarme a la licencia · {formatUsd(p.licenseOffer.amountCents)}
                    </Button>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {p.billed && p.plan === 'MULTISUCURSAL' && (
          <Card>
            <CardHeader title="Locales y franquicias" />
            <CardBody>
              <div className="bf-form" style={{ gap: 14 }}>
                <Facts>
                  <Fact label="Locales propios">
                    {p.branches.used} en uso · {p.branches.paid} pagos
                  </Fact>
                  {franchises && (
                    <Fact label="Franquicias">
                      {p.franchises.used} en uso · {p.franchises.paid} paga la marca
                      {p.franchises.pending > 0 && ` · ${p.franchises.pending} sin pagar su licencia`}
                    </Fact>
                  )}
                </Facts>
                <p className="bf-hint" style={{ margin: 0 }}>
                  {monthly ? (
                    <>
                      Tu abono trae la casa central y una sucursal. Cada local propio extra suma {formatUsd(p.prices.branchMonthly)} por mes
                      {franchises && ` y cada franquicia ${formatUsd(p.prices.franchiseMonthly)}`}. Al crearlo se cobra lo proporcional del mes,
                      así después pagás todo junto.
                    </>
                  ) : (
                    <>
                      Cada local propio extra suma {formatUsd(p.prices.branchYearly)} por año
                      {franchises && ` y cada franquicia ${formatUsd(p.prices.franchiseYearly)}`}. Al crearlo se cobra lo proporcional hasta tu
                      renovación, así después pagás todo junto.
                    </>
                  )}{' '}
                  Se suman desde <Link to="/sucursales">Sucursales</Link>.
                </p>
                {franchises && (
                  <label className="bf-form" style={{ gap: 6 }}>
                    <span className="bf-label">¿Quién paga {monthly ? 'el abono' : 'la licencia'} de cada franquicia nueva?</span>
                    <Select value={p.franchisePayer} disabled={payer.isPending} onChange={(e) => payer.mutate(e.target.value as FranchisePayer)}>
                      {(Object.keys(FRANCHISE_PAYER_LABELS) as FranchisePayer[]).map((k) => (
                        <option key={k} value={k}>
                          {FRANCHISE_PAYER_LABELS[k]}
                        </option>
                      ))}
                    </Select>
                    <span className="bf-hint">
                      {p.franchisePayer === 'BRAND'
                        ? `Cada franquicia nueva se suma a tu ${monthly ? 'abono mensual' : 'mantenimiento anual'}.`
                        : `Cada franquiciado paga ${monthly ? 'su abono por mes' : 'su licencia por año'} desde su usuario (Mi plan). A vos no se te cobra.`}{' '}
                      Las franquicias que ya tenés siguen como están.
                    </span>
                  </label>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {p.bifi && (p.billed || p.plan === 'BIFI') && (
          <BifiCard bifi={p.bifi} franchise={false} franchisesModule={franchises} busy={busy} onRun={run} onPortal={portal} ready={p.ready} />
        )}

        <Card>
          <CardHeader title="Módulos" subtitle="Se activan apenas se confirma el pago. bifi lleva además un abono mensual por número." />
          <CardBody>
            <div className="bf-form" style={{ gap: 12 }}>
              {active.map((m) => (
                <div key={m} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                  <span>{MODULE_LABELS[m]}</span>
                  <Badge tone="success" dot>
                    {m === 'ARCA' || (monthly && p.plan === 'MULTISUCURSAL' && SUBSCRIPTION_MULTI_MODULES.includes(m)) ? 'Incluido' : 'Activo'}
                  </Badge>
                </div>
              ))}
              {p.buyable.map((b) => (
                <div key={b.module} style={{ display: 'grid', gap: 6, paddingTop: 12, borderTop: '1px solid var(--bf-line)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                    <strong>{MODULE_LABELS[b.module]}</strong>
                    <Button
                      size="sm"
                      variant="primary"
                      loading={busy === b.module}
                      disabled={!p.ready || p.status === 'PAST_DUE'}
                      onClick={run(b.module, () => api.post<CheckoutResponse>('/billing/modules', { module: b.module }))}
                    >
                      Sumar · {b.priceCents ? formatUsd(b.priceCents) : ''}
                      {b.monthlyCents ? `${b.priceCents ? ' + ' : '+'}${formatUsd(b.monthlyCents)}/mes` : ''}
                    </Button>
                  </div>
                  {MODULE_HINTS[b.module] && (
                    <span className="bf-hint" style={{ margin: 0 }}>
                      {MODULE_HINTS[b.module]}
                    </span>
                  )}
                </div>
              ))}
              {!p.ready && p.buyable.length > 0 && (
                <p className="bf-hint" style={{ margin: 0 }}>
                  La compra online se habilita en breve. Mientras, escribinos a <a href="mailto:ventas@bifeai.com">ventas@bifeai.com</a>.
                </p>
              )}
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Tus datos" subtitle="Todo lo de tu carnicería, en planillas que se abren con Excel." />
          <CardBody>
            <p className="bf-hint" style={{ margin: '0 0 12px' }}>
              Productos, ventas, clientes, compras, proveedores, stock, empleados, facturas y más: un archivo por cada cosa, en un ZIP. Lo podés
              descargar cuando quieras, también si la cuenta está en solo lectura o dada de baja.
            </p>
            <Button icon={<Download />} loading={exporting} onClick={() => void exportData()}>
              Descargar mis datos
            </Button>
          </CardBody>
        </Card>
        <TermsCard />
      </div>
    </>
  );
}

/** Términos y condiciones: el dueño los firma dentro del sistema (versión, fecha e IP quedan guardadas). */
function TermsCard() {
  const { refresh } = useSession();
  const toast = useToast();
  const terms = useQuery({ queryKey: ['terms'], queryFn: () => api.get<TermsStatusDTO>('/billing/terms') });
  const [fullName, setFullName] = useState('');
  const [docId, setDocId] = useState('');
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sign = useMutation({
    mutationFn: () => api.post<TermsStatusDTO>('/billing/terms', { fullName, docId, accept }),
    onSuccess: async () => {
      toast.success('Términos firmados', 'Gracias. Quedó registrado.');
      await terms.refetch();
      await refresh();
    },
    onError: (err) => setError(errorMessage(err)),
  });
  useEffect(() => {
    if (window.location.hash === '#terminos') document.getElementById('terminos')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [terms.data]);
  const t = terms.data;
  if (!t) return null;
  return (
    <div id="terminos">
      <Card>
        <CardHeader title="Términos y condiciones" subtitle={`Versión ${t.version}`} />
        <CardBody>
          {t.signed ? (
            <p className="bf-hint" style={{ margin: 0 }}>
              <Badge tone="success" dot>
                Firmados
              </Badge>{' '}
              por {t.signed.fullName}
              {t.signed.docId ? ` (${t.signed.docId})` : ''} el {formatDate(t.signed.at)}
              {t.signed.source === 'WEB' ? ', al comprar en bifeai.com' : ''}.{' '}
              <a href={t.url} target="_blank" rel="noreferrer">
                Ver los términos <ExternalLink size={13} aria-hidden />
              </a>
            </p>
          ) : !t.canSign ? (
            <Callout tone="warning">Los términos los firma el dueño de la carnicería.</Callout>
          ) : (
            <div className="bf-form">
              <p className="bf-hint" style={{ margin: 0 }}>
                Leé los{' '}
                <a href={t.url} target="_blank" rel="noreferrer">
                  términos y condiciones
                </a>{' '}
                y la{' '}
                <a href={t.privacyUrl} target="_blank" rel="noreferrer">
                  política de privacidad
                </a>{' '}
                de BifeAI. Para firmarlos, completá tus datos: queda registrada la fecha y desde dónde los firmaste.
              </p>
              {error && <Callout tone="danger">{error}</Callout>}
              <div className="bf-form-row">
                <Field label="Nombre y apellido">
                  <Input value={fullName} onChange={(e) => setFullName(e.target.value)} maxLength={120} autoComplete="name" />
                </Field>
                <Field label="DNI o CUIT">
                  <Input value={docId} onChange={(e) => setDocId(e.target.value)} maxLength={15} inputMode="numeric" />
                </Field>
              </div>
              <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} style={{ marginTop: 4 }} />
                <span>Leí y acepto los términos y condiciones y la política de privacidad de BifeAI, en nombre de la carnicería.</span>
              </label>
              <div className="bf-form-actions">
                <Button
                  variant="primary"
                  loading={sign.isPending}
                  disabled={!accept || fullName.trim().length < 3 || docId.trim().length < 7}
                  onClick={() => {
                    setError(null);
                    sign.mutate();
                  }}
                >
                  Firmar
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

/** bifi por número: lo que paga quien mira, el precio por número y quién paga el de las franquicias. */
function BifiCard({
  bifi,
  franchise,
  franchisesModule = false,
  busy,
  onRun,
  onPortal,
  ready,
}: {
  bifi: NonNullable<MyPlanDTO['bifi']>;
  franchise: boolean;
  franchisesModule?: boolean;
  busy: string | null;
  onRun: (key: string, p: () => Promise<CheckoutResponse>) => () => Promise<void>;
  onPortal: () => Promise<void>;
  ready: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const payer = useMutation({
    mutationFn: (p: FranchisePayer) => api.put('/billing/bifi/payer', { payer: p }),
    onSuccess: async () => {
      toast.success('Listo', 'Los abonos de bifi ya se actualizaron.');
      await qc.invalidateQueries({ queryKey: ['my-plan'] });
    },
    onError: (err) => toast.error('No se pudo cambiar', errorMessage(err)),
  });
  const price = (
    <>
      {formatUsd(bifi.unitCents)} por mes por número
      {bifi.volume && (
        <>
          {' '}
          <Badge tone="success">Precio por volumen</Badge>
        </>
      )}
    </>
  );

  if (franchise) {
    const mine = bifi.payer === 'FRANCHISEE';
    const seat = bifi.seat;
    const live = seat && (seat.status === 'ACTIVE' || seat.status === 'PAST_DUE');
    return (
      <Card>
        <CardHeader title="bifi de tu franquicia" />
        <CardBody>
          <div className="bf-form" style={{ gap: 14 }}>
            {!mine ? (
              <p style={{ margin: 0 }}>El bifi de tu franquicia lo paga la marca. No tenés nada para pagar.</p>
            ) : (
              <>
                <Facts>
                  <Fact label="Estado">
                    <Badge tone={live ? (seat.status === 'ACTIVE' ? 'success' : 'danger') : 'warning'} dot>
                      {live ? (seat.status === 'ACTIVE' ? 'Activo' : 'Pago pendiente') : 'Sin activar'}
                    </Badge>
                  </Fact>
                  {live && <Fact label="Números">{bifi.lines}</Fact>}
                  <Fact label="Precio">{price}</Fact>
                </Facts>
                {!live ? (
                  <>
                    <p className="bf-hint" style={{ margin: 0 }}>
                      Para conectar el WhatsApp de tu franquicia, activá tu abono: pagás el primer mes ahora con tarjeta y después se cobra solo cada
                      mes, por cada número que conectes. La activación de bifi ya la pagó la marca.
                    </p>
                    <div>
                      <Button
                        variant="primary"
                        icon={<CreditCard />}
                        loading={busy === 'bifi-seat'}
                        disabled={!ready}
                        onClick={onRun('bifi-seat', () => api.post<CheckoutResponse>('/billing/bifi/seat/checkout'))}
                      >
                        Activar bifi · {formatUsd(bifi.unitCents)} por mes
                      </Button>
                    </div>
                  </>
                ) : (
                  <div>
                    <Button icon={<ExternalLink />} loading={busy === 'portal'} onClick={onPortal}>
                      Tarjeta y facturas
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="bifi" subtitle="Un abono por cada número de WhatsApp conectado." />
      <CardBody>
        <div className="bf-form" style={{ gap: 14 }}>
          <Facts>
            <Fact label="Números que pagás">{bifi.lines}</Fact>
            {franchisesModule && <Fact label="Números en toda la red">{bifi.networkLines}</Fact>}
            <Fact label="Precio">{price}</Fact>
          </Facts>
          <p className="bf-hint" style={{ margin: 0 }}>
            Si un número atiende varias sucursales, paga uno solo. Al conectar un número nuevo se cobra lo proporcional del mes; si lo desconectás,
            deja de cobrarse desde el mes siguiente. Con más de 10 números en la red, todos pasan al precio por volumen.
          </p>
          {franchisesModule && (
            <label className="bf-form" style={{ gap: 6 }}>
              <span className="bf-label">¿Quién paga el bifi de cada franquicia?</span>
              <Select value={bifi.payer} disabled={payer.isPending} onChange={(e) => payer.mutate(e.target.value as FranchisePayer)}>
                {(Object.keys(FRANCHISE_PAYER_LABELS) as FranchisePayer[]).map((k) => (
                  <option key={k} value={k}>
                    {FRANCHISE_PAYER_LABELS[k]}
                  </option>
                ))}
              </Select>
              <span className="bf-hint">
                {bifi.payer === 'BRAND'
                  ? 'Los números de las franquicias se suman a tu abono de bifi.'
                  : 'Cada franquiciado activa y paga su abono desde su usuario (Mi plan). Lo que ya pagaste del mes sigue valiendo hasta fin de mes.'}
              </span>
            </label>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function DunningCallout({ tone, title, text, children }: { tone: 'warning' | 'danger'; title: string; text: string; children?: ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <Callout tone={tone}>
        <strong>{title}.</strong> {text}
        {children}
      </Callout>
    </div>
  );
}

function Facts({ children }: { children: ReactNode }) {
  return <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 20px', margin: 0 }}>{children}</dl>;
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="bf-cell-sub">{label}</dt>
      <dd style={{ margin: 0 }}>{children}</dd>
    </>
  );
}
